import { useQuery } from '@tanstack/react-query'
import { conciergeFireService, firerouterService } from '@chilipiper/service'
import { defaultErrorCallback, get } from '@chilipiper/service/src/api'
import { QueueAlgorithm, SlugMappingResponse } from '@chilipiper/api-type-def'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { getDomain, parseQuery } from '@chilipiper/utils'
import { useEffect } from 'react'
import { RouteLocation } from '../types'

const useGetTenantIdFromSubdomain = () => {
  const domain = getDomain()
  const query = useQuery({
    queryKey: ['migration', 'get-tenant-id', domain],
    queryFn: () => {
      return get(`tenant-by-subdomain/${domain}`).json<string>()
    },
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as any)
    }
  }, [query.error])

  return query
}

const useFireToggle = () => {
  const domain = getDomain()
  const { data: tenantId } = useGetTenantIdFromSubdomain()
  const query = useQuery({
    queryKey: ['migration', 'use-fire', 'toggle', domain],
    queryFn: () => conciergeFireService.getFireToggle(tenantId as string),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as any)
    }
  }, [query.error])

  return query
}

const isBookingLink = (pathname: string, params: URLSearchParams) =>
  pathname.startsWith('/book') && !params.has('rescheduleId') && !pathname.includes('/dialer/')
const isPersonalBookingLink = (queueSlug: string) => queueSlug === 'me'
const isRouterLink = (pathname: string) => pathname.startsWith('/router')

const handleMappingRedirect = (
  slugResponse?: SlugMappingResponse,
  typeResponse?: SlugMappingResponse,
  byResponse?: SlugMappingResponse,
  additionalSearchParams?: URLSearchParams,
  isPersonalWorkspace?: boolean
) => {
  const searchParams = new URLSearchParams(additionalSearchParams)
  if (!slugResponse?.fireSlug) {
    return
  }
  const baseUrl = window.location.origin.replace('.na', '')
  switch (slugResponse.entityType) {
    case 'router':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      if (byResponse?.fireSlug) {
        searchParams.set('by', byResponse.fireSlug)
      }
      window.location.href = `${baseUrl}/concierge-router/link/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'user':
      window.location.href = `${baseUrl}/${
        isPersonalWorkspace || !typeResponse ? 'me' : 'shared'
      }/${slugResponse.fireSlug}${typeResponse ? `/${typeResponse.fireSlug}` : ''}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'queue':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      window.location.href = `${baseUrl}/round-robin/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'ownershipSchedulingLink':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      window.location.href = `${baseUrl}/ownership/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'groupSchedulingLink':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      window.location.href = `${baseUrl}/group/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    default:
      break
  }
  return null
}

export const useFireRedirect = (
  isLoading?: boolean,
  isPersonalWorkspace?: boolean,
  meetingTypeSlug?: string,
  queueAlgorithm?: QueueAlgorithm,
  formId?: string
) => {
  const location = useLocation() as RouteLocation
  const routeMatch = useRouteMatch()
  const query = parseQuery()
  const domain = getDomain()
  const type = meetingTypeSlug || query.type

  const { data: useAllProductsFire, isLoading: isLoadingFireToggle } = useFireToggle()

  const {
    data,
    isLoading: isLoadingRedirect,
    fetchStatus,
    error,
  } = useQuery({
    queryKey: [
      'migration',
      'redirect',
      domain,
      location.pathname,
      window.location.search,
      isPersonalWorkspace,
      queueAlgorithm,
      type,
      query.by,
    ],
    queryFn: () => {
      if (!location.pathname) {
        return Promise.resolve(null)
      }
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.delete('type')
      searchParams.delete('domain')
      let slugPromise: Promise<SlugMappingResponse | undefined> = Promise.resolve(undefined)
      if (isBookingLink(location.pathname, searchParams)) {
        const [path] = Object.values<string>(routeMatch.params)
        const [queueSlug, userSlug] = path.split('/')
        if (isPersonalBookingLink(queueSlug)) {
          slugPromise = firerouterService.mapping(domain, 'user', userSlug)
        } else {
          void firerouterService.mapping(domain, 'user', userSlug).then(({ fireSlug }) => {
            if (fireSlug) {
              searchParams.set('assignee', fireSlug)
            }
          })
          if (queueAlgorithm === 'Group') {
            slugPromise = firerouterService.mapping(domain, 'groupSchedulingLink', queueSlug)
          } else if (queueAlgorithm === 'Ownership') {
            slugPromise = firerouterService.mapping(domain, 'ownershipSchedulingLink', queueSlug)
          } else {
            slugPromise = firerouterService.mapping(domain, 'queue', queueSlug)
          }
        }
      } else if (isRouterLink(location.pathname)) {
        const [routerName] = Object.values<string>(routeMatch.params)
        slugPromise = firerouterService.mapping(domain, 'router', routerName)
      } else {
        return Promise.resolve(undefined)
      }
      const promises: Promise<SlugMappingResponse | undefined>[] = [slugPromise]
      promises.push(
        query.type
          ? firerouterService.mapping(domain, 'adminSchedulingLink', type)
          : Promise.resolve(undefined)
      )
      promises.push(
        query.by ? firerouterService.mapping(domain, 'user', query.by) : Promise.resolve(undefined)
      )
      if (formId) {
        Array.from(searchParams.entries()).forEach(([key]) => {
          promises.push(
            firerouterService.mappingByLegacyId(domain, 'form.block.mapping', `${formId}.${key}`)
          )
        })
      }
      return Promise.all(promises).then(
        ([slugResponse, typeResponse, byResponse, ...blockResponses]) => {
          const newSearchParams = new URLSearchParams(searchParams)
          blockResponses.forEach(block => {
            if (block?.entityFireId && block.entityLegacyId) {
              newSearchParams.set(
                block.entityFireId,
                searchParams.get(block.entityLegacyId.replace(`${formId}.`, '')) || ''
              )
            }
          })
          return handleMappingRedirect(
            slugResponse,
            typeResponse,
            byResponse,
            newSearchParams,
            isPersonalWorkspace
          )
        }
      )
    },
    enabled: Boolean(useAllProductsFire) && !isLoading,
  })

  useEffect(() => {
    if (error) {
      defaultErrorCallback(error as any)
    }
  }, [error])

  return {
    data,
    isLoading: isLoadingFireToggle || (isLoadingRedirect && fetchStatus !== 'idle'),
  }
}
